/**
 * CRM Contact List View — enhanced contact list with CRM metrics,
 * tab navigation, sorting, and click-through to detail.
 */

import { useEffect, useCallback, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Users, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { formatCents } from '@/utils/currency'
import { SortableHeader } from '@/components/ui/SortableHeader'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
  Spinner,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Badge,
  Switch,
} from '@/components/ui'

export function ContactListView() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const segmentId = searchParams.get('segment')
  const { fetchContacts, fetchSegmentContacts } = useCRM()
  const contacts = useCrmStore((s) => s.contacts)
  const total = useCrmStore((s) => s.contactTotal)
  const totalPages = useCrmStore((s) => s.contactTotalPages)
  const isLoading = useCrmStore((s) => s.isLoadingContacts)
  const filters = useCrmStore((s) => s.contactFilters)
  const setFilters = useCrmStore((s) => s.setContactFilters)
  const activeSegment = useCrmStore((s) => s.activeSegment)

  const [searchInput, setSearchInput] = useState(filters.search)

  // Fetch: if segment is active, use segment endpoint; otherwise, normal contacts
  useEffect(() => {
    if (segmentId) {
      fetchSegmentContacts(segmentId, filters.page)
    } else {
      fetchContacts(filters)
    }
  }, [fetchContacts, fetchSegmentContacts, segmentId, filters.page, filters.channel, filters.tag, filters.sortBy, filters.sortOrder, filters.blocked, filters.includeDeleted])

  // Debounced search (only when not filtering by segment)
  useEffect(() => {
    if (segmentId) return
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters({ search: searchInput, page: 1 })
        fetchContacts({ search: searchInput, page: 1 })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, segmentId])

  const clearSegmentFilter = useCallback(() => {
    searchParams.delete('segment')
    setSearchParams(searchParams)
    useCrmStore.getState().setActiveSegment(null)
  }, [searchParams, setSearchParams])

  const handlePageChange = useCallback((newPage: number) => {
    setFilters({ page: newPage })
  }, [setFilters])

  const handleSort = useCallback((sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters({ sortBy, sortOrder, page: 1 })
    fetchContacts({ sortBy, sortOrder, page: 1 })
  }, [setFilters, fetchContacts])

  const formatRelativeTime = (isoDate: string | null) => {
    if (!isoDate) return '—'
    const date = new Date(isoDate)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHrs = Math.floor(diffMin / 60)
    if (diffHrs < 24) return `${diffHrs}h ago`
    const diffDays = Math.floor(diffHrs / 24)
    if (diffDays < 30) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border">
        {/* Segment filter banner */}
        {segmentId && activeSegment && (
          <div className="px-3 py-2 flex items-center gap-2 rounded-md bg-info/10">
            {activeSegment.color && (
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: activeSegment.color }} />
            )}
            <span className="text-sm font-medium text-info">
              Segment: {activeSegment.name}
            </span>
            <span className="text-xs text-info">({total} contacts)</span>
            <button
              onClick={clearSegmentFilter}
              className="ml-auto p-1 rounded-sm hover:bg-info/20"
            >
              <X className="w-3.5 h-3.5 text-info" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="py-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search contacts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={filters.channel || 'all'}
            onValueChange={(v) => setFilters({ channel: v === 'all' ? null : v, page: 1 })}
          >
            <SelectTrigger size="sm" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="web">Web</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.blocked === null ? 'all' : filters.blocked ? 'blocked' : 'active'}
            onValueChange={(v) => setFilters({ blocked: v === 'all' ? null : v === 'blocked', page: 1 })}
          >
            <SelectTrigger size="sm" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="active">Not Blocked</SelectItem>
            </SelectContent>
          </Select>

          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
            <Switch
              checked={filters.includeDeleted}
              onCheckedChange={(checked) => setFilters({ includeDeleted: checked, page: 1 })}
              aria-label="Show deleted"
            />
            Show deleted
          </label>

          {/* Sort is now controlled by clicking column headers */}
        </div>
      </div>

      {/* Contact table */}
      <div className="flex-1 overflow-auto">
        {isLoading && contacts.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : contacts.length === 0 ? (
          <EmptyState icon={Users} title="No contacts found" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader label="Name" sortKey="name" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={handleSort} className="px-6" />
                <TableHead>Phone / ID</TableHead>
                <TableHead>Channel</TableHead>
                <SortableHeader label="LTV" sortKey="ltv" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={handleSort} align="right" />
                <SortableHeader label="Orders" sortKey="orders" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={handleSort} align="right" />
                <SortableHeader label="Last Active" sortKey="last_seen" currentSortBy={filters.sortBy} currentSortOrder={filters.sortOrder} onSort={handleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                  className="hover:bg-muted cursor-pointer"
                >
                  <TableCell className="px-6 py-3">
                    <div>
                      <div className="font-medium text-foreground">
                        {contact.displayName || contact.channelUserId}
                      </div>
                      {(contact.isBlocked || contact.isDeleted) && (
                        <div className="flex gap-1 mt-1">
                          {contact.isBlocked && <Badge variant="destructive">Blocked</Badge>}
                          {contact.isDeleted && <Badge variant="secondary">Deleted</Badge>}
                        </div>
                      )}
                      {contact.tags && contact.tags.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {contact.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary">
                              {tag}
                            </Badge>
                          ))}
                          {contact.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{contact.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {contact.channelUserId}
                  </TableCell>
                  <TableCell>
                    <Badge variant={contact.channel === 'whatsapp' ? 'success' : 'info'}>
                      {contact.channel}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-foreground font-medium">
                    {contact.totalSpentCents > 0
                      ? formatCents(contact.totalSpentCents, contact.currency)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {contact.totalOrders || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {formatRelativeTime(contact.lastSeenAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sticky bottom-0 bg-background border-t border-border py-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {(filters.page - 1) * filters.perPage + 1}–
            {Math.min(filters.page * filters.perPage, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page <= 1}
              className="p-1.5 rounded-sm hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-foreground min-w-[80px] text-center">
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= totalPages}
              className="p-1.5 rounded-sm hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
